import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Badge } from "@/components/ui/badge"
import { useOrgSettings } from "@/org_settings/state.tsx"
import { useState } from "preact/hooks"
import { Link, useLocation } from "wouter-preact"
import { AccountingCheckAutocomplete } from "./AccountingCheckAutocomplete"
import { AccountingCheckJson, TransactionJson } from "@/api/accounting.tsx"
import { DollarSign, AlertCircle, CheckCircle, ExternalLink, FileText } from "lucide-react"

import { applied_remote_link } from "@/accounting/cash_application_display.ts"
import { MonthEndJournalEntryControl } from "./MonthEndJournalEntryControl"
import { check_apply_path } from "./check_path"
import { ManualAppliedToggle } from "./manual_applied_toggle"

const INTEGRATION_LABELS: Record<string, string> = {
  sageintacct: "Sage Intacct",
  quickbooks: "QuickBooks",
  xero: "Xero",
  netsuite: "NetSuite",
}

export function get_integration_label(integration: string | null): string {
  if (!integration) return "Accounting"
  return INTEGRATION_LABELS[integration.toLowerCase()] || integration
}

/** Green "applied" pill — an external link when the accounting system has a deep link, else static. */
function AppliedIndicator({ label, href }: { label: string; href: string | null }) {
  const button = (
    <Button variant="outline" class="gap-2 text-green-700 border-green-200">
      {href ? <ExternalLink className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
      {label}
    </Button>
  )
  return href ? (
    <a href={href} target="_blank" rel="noreferrer">
      {button}
    </a>
  ) : (
    button
  )
}

/** Only uncoded **deduction** lines affect the pre–Apply Cash warning; uncoded invoices are assigned on the Apply page. */
function transaction_needs_cash_apply_coding_warning(t: TransactionJson): boolean {
  return t.kind === "deduction" && !t.coded
}

interface CheckData {
  id?: string
  number: string
  amount?: number
  date?: string
  /** Deduction source for this check (e.g. `unfi`) — ranks matching QuickBooks customers first. */
  source?: string
  transactions: TransactionJson[]
  credit_memo_id?: string
  /** QBO Payment Id once applied (cash applied in QuickBooks). Present = applied. */
  applied_payment_remote_id?: string
  /** QBO CreditMemo Id when applied was detected via a synced credit memo. Present = applied. */
  applied_credit_memo_remote_id?: string
  /** Set with no remote ids when a user marked the check applied by hand. */
  applied_at?: string
}

interface CashApplicationControlProps {
  check: CheckData
  onNavigateToUnassigned?: (deductionId: string) => void
  /** Called after the check is manually marked applied / not applied. */
  onAppliedChange?: () => void
}

export function CashApplicationControl({ check, onNavigateToUnassigned, onAppliedChange }: CashApplicationControlProps) {
  const orgSettings = useOrgSettings()
  const [, setLocation] = useLocation()
  const [selected_accounting_check, set_selected_accounting_check] = useState<AccountingCheckJson | null>(null)
  const [popover_open, set_popover_open] = useState(false)
  const [confirm_uncoded_open, set_confirm_uncoded_open] = useState(false)

  const uncoded_count = check.transactions.filter(transaction_needs_cash_apply_coding_warning).length
  const all_coded = uncoded_count === 0

  const integration_label = get_integration_label(orgSettings.accounting_integration)

  function navigate_to_apply() {
    if (!check.id) return
    // Selecting an existing accounting payment is optional — it's only a hint. The Apply page
    // lets the user create the payment (pick customer + deposit account) when none is matched.
    setLocation(check_apply_path(check.number, selected_accounting_check?.id))
  }

  function handle_apply_click() {
    if (!check.id) return
    if (!all_coded) {
      set_confirm_uncoded_open(true)
    } else {
      navigate_to_apply()
    }
  }

  function handle_unassigned_click() {
    const first_unassigned = check.transactions.find(transaction_needs_cash_apply_coding_warning)
    if (first_unassigned && onNavigateToUnassigned) {
      onNavigateToUnassigned(first_unassigned.id)
    }
  }

  // If the check is already applied (cash applied), show the payment in the accounting system.
  if (check.applied_payment_remote_id) {
    return (
      <AppliedIndicator
        label={`Payment created in ${integration_label}`}
        href={applied_remote_link(orgSettings.accounting_integration, "payment", check.applied_payment_remote_id)}
      />
    )
  }

  // Applied was detected via an existing credit memo synced from the accounting system. For
  // NetSuite, the deduction GL detail still needs to be posted at month end — as a journal entry
  // (a second credit memo would double-count the deduction).
  if (check.applied_credit_memo_remote_id) {
    const is_netsuite = (orgSettings.accounting_integration ?? "").toLowerCase() === "netsuite"
    return (
      <div class="flex items-center gap-2">
        <AppliedIndicator
          label={`Credit memo created in ${integration_label}`}
          href={applied_remote_link(orgSettings.accounting_integration, "credit_memo", check.applied_credit_memo_remote_id)}
        />
        {is_netsuite && check.id ? <MonthEndJournalEntryControl checkId={check.id} /> : null}
      </div>
    )
  }

  function handle_applied_change() {
    onAppliedChange?.()
  }

  // Marked applied by hand (cash applied outside Promoted, e.g. brands exporting CSV). Undoable.
  if (check.applied_at) {
    return (
      <div class="flex items-center gap-2">
        <AppliedIndicator label="Applied (manual)" href={null} />
        <ManualAppliedToggle
          check_id={check.id}
          check_number={check.number}
          state="manual"
          on_change={handle_applied_change}
        />
      </div>
    )
  }

  // A credit memo exists but the check isn't applied (an earlier apply attempt didn't finish).
  // Keep the apply flow reachable — it's resumable (reuses the memo, skips an already-done publish,
  // and continues to the payment step) — alongside the link to the memo.
  if (check.credit_memo_id) {
    return (
      <div class="flex items-center gap-2">
        <Link to={`/credit-memo/${check.credit_memo_id}`}>
          <Button variant="outline" class="gap-2">
            <FileText className="h-4 w-4" />
            View Credit Memo
          </Button>
        </Link>
        <Button
          onClick={navigate_to_apply}
          disabled={!check.id}
          class="gap-2 bg-green-600 hover:bg-green-700"
        >
          <DollarSign className="h-4 w-4" />
          Preview cash application
        </Button>
      </div>
    )
  }

  const mark_applied_toggle = (
    <ManualAppliedToggle
      check_id={check.id}
      check_number={check.number}
      state="not_applied"
      on_change={handle_applied_change}
    />
  )

  if (!orgSettings.accounting_integration) {
    return (
      <div class="flex items-center gap-2">
        {mark_applied_toggle}
        <Popover open={popover_open} onOpenChange={set_popover_open}>
          <PopoverTrigger asChild>
            <Button variant="outline" class="gap-2">
              <DollarSign className="h-4 w-4" />
              Preview cash application
            </Button>
          </PopoverTrigger>
          <PopoverContent class="w-80">
            <div class="space-y-3">
              <div class="font-medium">Connect Your Accounting System</div>
              <p class="text-sm text-gray-600">
                To apply cash to checks, connect your accounting system (QuickBooks, Xero, Sage Intacct, or NetSuite).
              </p>
              <Link to="/settings/connections">
                <Button variant="outline" class="w-full gap-2">
                  <ExternalLink className="h-4 w-4" />
                  Go to Connections
                </Button>
              </Link>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    )
  }

  const can_apply = !!check.id

  return (
    <div class="flex items-center gap-2">
      <div class="w-64">
        <AccountingCheckAutocomplete
          checkId={check.id}
          selectedCheckId={selected_accounting_check?.id}
          onSelectCheck={set_selected_accounting_check}
          matchHints={{
            checkNumber: check.number,
            amountDollars: check.amount,
            checkDateIso: check.date,
            deductionSource: check.source,
          }}
        />
      </div>

      {uncoded_count > 0 && (
        <Badge
          variant="outline"
          class="gap-1 text-orange-600 border-orange-200 cursor-pointer"
          onClick={handle_unassigned_click}
        >
          <AlertCircle className="h-3 w-3" />
          {uncoded_count} uncoded
        </Badge>
      )}

      {/* Uncoded confirmation popover */}
      <Popover open={confirm_uncoded_open} onOpenChange={set_confirm_uncoded_open}>
        <PopoverTrigger asChild>
          <Button
            onClick={handle_apply_click}
            disabled={!can_apply}
            variant={can_apply ? "default" : "outline"}
            class={`gap-2 ${can_apply ? "bg-green-600 hover:bg-green-700" : ""}`}
          >
            <DollarSign className="h-4 w-4" />
            Preview cash application
          </Button>
        </PopoverTrigger>
        <PopoverContent class="w-80">
          <div class="space-y-3">
            <div class="font-medium">Uncoded Deductions</div>
            <p class="text-sm text-gray-600">
              {uncoded_count} deduction{uncoded_count !== 1 ? "s are" : " is"} still uncoded. The preview will ask you to
              code them before the check can be applied, unless a holding account is set, in which case they park there
              and are coded later from Open deductions. Continue to the preview?
            </p>
            <div class="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => set_confirm_uncoded_open(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                class="bg-green-600 hover:bg-green-700"
                onClick={() => {
                  set_confirm_uncoded_open(false)
                  navigate_to_apply()
                }}
              >
                Continue to preview
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {mark_applied_toggle}
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("transaction_needs_cash_apply_coding_warning", () => {
    it("is false for uncoded invoice", () => {
      expect(
        transaction_needs_cash_apply_coding_warning({
          kind: "invoice",
          coded: false,
        } as unknown as TransactionJson)
      ).toBe(false)
    })

    it("is true for uncoded deduction", () => {
      expect(
        transaction_needs_cash_apply_coding_warning({
          kind: "deduction",
          coded: false,
        } as unknown as TransactionJson)
      ).toBe(true)
    })

    it("is false for coded deduction", () => {
      expect(
        transaction_needs_cash_apply_coding_warning({
          kind: "deduction",
          coded: true,
        } as unknown as TransactionJson)
      ).toBe(false)
    })
  })
}
